<script setup>
import { computed } from 'vue'

const props = defineProps({
    isOpen: { type: Boolean, default: false },
    membershipStatus: { type: String, default: 'expired' },
    intent: { type: String, default: '' },
    performerName: { type: String, default: '' },
    performerPhoto: { type: String, default: '' },
    // Only set when the rejected purchase was for a single, specific
    // product (not a multi-item context) — drives the product-level
    // upgrade-attribution cookies stamped in onCtaClick below. Mirrors
    // membership_upsell_modal.blade.php's currentProduct handling.
    productId: { type: [Number, String], default: null },
    productTitle: { type: String, default: '' },
    productKind: { type: String, default: '' },
    productStars: { type: [Number, String], default: null },
    performerId: { type: [Number, String], default: null },
    // The surface this modal was opened on, stamped into upgrade_surface on CTA
    // click so a wall-driven upgrade is attributable to WHERE it happened and
    // not just to the wall. Matches the `source` the page's own wall events
    // carry, so a report can join the two.
    source: { type: String, default: '' },
})

const emit = defineEmits(['close', 'cta'])

const STAR_IMG = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

/**
 * Copy matrix: membershipStatus (trial|expired) supplies the problem/action/CTA,
 * intent (what the member was trying to buy) supplies the title and hook.
 * Keep in sync with resources/views/members3/partials/membership_upsell_modal.blade.php.
 */
const STATUS_COPY = {
    trial: {
        problem: 'Star Shop purchases are for full members',
        action: 'Upgrade your trial to finish this purchase',
        fallback: 'Star Shop purchases are for full members. Upgrade your trial to complete this purchase — your stars are still yours.',
        cta: 'Upgrade',
        bonusPerk: 'Bonus Stars when you upgrade',
    },
    expired: {
        problem: 'your membership isn’t active',
        action: 'Renew now to finish this purchase',
        fallback: 'Your membership isn’t active. Renew now to complete this purchase — your stars are still yours.',
        cta: 'Reactivate',
        bonusPerk: 'Bonus Stars when you reactivate',
    },
}

const INTENT_COPY = {
    discount:       { title: 'Claim Her Discount',         hook: (name) => name ? `${name}’s discount is ready to claim` : 'That discount is ready to claim' },
    flowers:        { title: 'Send Her Flowers',           hook: (name) => name ? `Your bouquet for ${name} is ready to send` : 'Your bouquet is ready to send' },
    rebook:         { title: 'Help Bring Her Back',        hook: (name) => `Your stars can help bring ${name || 'her'} back to Naughty America` },
    remaster:       { title: 'Back This Remaster',         hook: () => 'Your stars can help restore this classic' },
    xr:             { title: 'Make It Immersive',          hook: (name) => name ? `Your stars can help bring ${name} into VR & AR` : 'Your stars can help fund this immersive experience' },
    series:         { title: 'Revive This Series',         hook: () => 'Your stars can help bring this series back' },
    choice:         { title: 'Shape What’s Next',          hook: () => 'Your stars help decide what we shoot next' },
    community_goal: { title: 'Back This Goal',             hook: () => 'Your stars can help make this goal happen' },
    direct_access:  { title: 'Unlock Her Exclusive Clips', hook: (name) => name ? `${name}’s exclusive clips are one step away` : 'Those exclusive clips are one step away' },
    cart:           { title: 'Your Cart Is Ready',         hook: () => 'Your picks are saved and ready to check out' },
}

const copy = computed(() => {
    const status = STATUS_COPY[props.membershipStatus] ?? STATUS_COPY.expired
    const intent = INTENT_COPY[props.intent]
    if (!intent) {
        return { title: 'Unlock the Star Shop', subtitle: status.fallback, cta: status.cta, bonusPerk: status.bonusPerk }
    }
    return {
        title: intent.title,
        subtitle: `${intent.hook(props.performerName)}, but ${status.problem}. ${status.action} — your stars are still yours.`,
        cta: status.cta,
        bonusPerk: status.bonusPerk,
    }
})

/**
 * Stamp the attribution cookies read back by the upgrade receipt pages:
 * upgrade_click_src feeds "Member Upgraded", upgrade_source feeds "Trial Upgraded"
 * (see MemberPackageReceiptHelper), so wall-driven upgrades are attributable in Tinybird.
 * When productId is set (a single specific product, not a multi-item
 * context), also stamp which one drove it — same star_shop_upgrade_product_*
 * cookies membership_upsell_modal.blade.php's Blade twin and
 * star_balance_reminder.blade.php's trial/expired teaser use, read back by
 * the same helper fields.
 */
function onCtaClick() {
    if (window.LTIAnalytics?.setCookie) {
        const thirtyMinutesInDays = 30 / (24 * 60)
        window.LTIAnalytics.setCookie('upgrade_click_src', 'star_shop_membership_wall', thirtyMinutesInDays)
        window.LTIAnalytics.setCookie('upgrade_source', 'star_shop_membership_wall', thirtyMinutesInDays)
        // See membership_upsell_modal.blade.php's twin of this: upgrade_source
        // is surface-agnostic by design, so the surface rides separately.
        if (props.source) {
            window.LTIAnalytics.setCookie('upgrade_surface', props.source, thirtyMinutesInDays)
        }
        if (props.productId) {
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_id', props.productId, thirtyMinutesInDays)
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_title', props.productTitle, thirtyMinutesInDays)
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_kind', props.productKind, thirtyMinutesInDays)
            window.LTIAnalytics.setCookie('star_shop_upgrade_product_stars', props.productStars, thirtyMinutesInDays)
            if (props.performerId) {
                window.LTIAnalytics.setCookie('star_shop_upgrade_product_performer_id', props.performerId, thirtyMinutesInDays)
            }
        }
    }
    emit('cta')
}

const upgradeUrl = computed(() => {
    const url = new URL(route('members2.account.upgrade'), window.location.origin)
    url.searchParams.set('psc2', window.location.pathname + window.location.search)
    url.searchParams.set('psc3', 'star_shop_membership_wall')
    return url.pathname + url.search
})
</script>

<template>
    <Teleport to="body">
    <div v-if="isOpen" class="mum-overlay">
        <div class="mum-scroll" @click.self="emit('close')">
            <div class="mum-backdrop" @click="emit('close')"></div>
            <div class="mum-content">
                <div class="mum-stripe" aria-hidden="true"></div>
                <button class="mum-close" aria-label="Close" @click="emit('close')">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>

                <div class="mum-header">
                    <div v-if="performerPhoto" class="mum-photo-wrap">
                        <img :src="performerPhoto" :alt="performerName || 'Performer'" class="mum-photo">
                        <img :src="STAR_IMG" alt="" class="mum-photo-star">
                    </div>
                    <img v-else :src="STAR_IMG" alt="Star" class="mum-star-icon">
                    <span class="mum-badge">Members-Only Perk</span>
                    <h2 class="mum-title">{{ copy.title }}</h2>
                    <p class="mum-subtitle">{{ copy.subtitle }}</p>
                </div>

                <ul class="mum-perks">
                    <li>Redeem your stars on exclusive Star Shop rewards</li>
                    <li>Back community goals and castings</li>
                    <li>Full access to over 13k videos</li>
                    <li>{{ copy.bonusPerk }}</li>
                </ul>

                <a :href="upgradeUrl" class="mum-cta" @click="onCtaClick">{{ copy.cta }}</a>
                <p class="mum-trust">Instant access &middot; Your stars stay in your balance</p>
                <button type="button" class="mum-dismiss" @click="emit('close')">Maybe later</button>
            </div>
        </div>
    </div>
    </Teleport>
</template>

<style scoped>
.mum-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000020;
}
.mum-scroll {
    position: absolute;
    inset: 0;
    overflow-y: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}
.mum-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(4, 10, 24, 0.82);
    backdrop-filter: blur(3px);
}
.mum-content {
    position: relative;
    overflow: hidden;
    background: linear-gradient(165deg, #13203d 0%, #0c142b 60%, #180d18 100%);
    border: 1px solid rgba(200, 16, 46, 0.5);
    border-radius: 16px;
    padding: 0 26px 22px;
    width: 100%;
    max-width: 400px;
    text-align: center;
    color: #fff;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.65);
}
.mum-stripe {
    height: 4px;
    margin: 0 -26px 26px;
    background: linear-gradient(90deg, #C8102E 0%, #C8102E 32%, #f4f6fb 46%, #f4f6fb 54%, #12356E 68%, #12356E 100%);
}
.mum-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 30px;
    height: 30px;
    padding: 5px;
    background: none;
    border: none;
    color: #8fa3c4;
    cursor: pointer;
}
.mum-close:hover { color: #fff; }
.mum-star-icon {
    display: block;
    width: 58px;
    height: 58px;
    margin: 0 auto 12px;
    filter: drop-shadow(0 0 14px rgba(200, 16, 46, 0.55));
}
.mum-photo-wrap {
    position: relative;
    width: 74px;
    height: 74px;
    margin: 0 auto 12px;
}
.mum-photo {
    width: 74px;
    height: 74px;
    border-radius: 50%;
    object-fit: cover;
    object-position: top;
    border: 2px solid rgba(200, 16, 46, 0.6);
    box-shadow: 0 0 18px rgba(200, 16, 46, 0.45);
}
.mum-photo-star {
    position: absolute;
    bottom: -4px;
    right: -7px;
    width: 30px;
    height: 30px;
    filter: drop-shadow(0 0 8px rgba(200, 16, 46, 0.55));
}
.mum-badge {
    display: inline-block;
    background: #12356E;
    color: #dbe6ff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: 999px;
    border: 1px solid rgba(219, 230, 255, 0.25);
    margin-bottom: 12px;
}
.mum-title {
    font-size: 24px;
    font-weight: 800;
    margin: 0 0 8px;
    color: #fff;
    letter-spacing: -0.01em;
}
.mum-subtitle {
    font-size: 14px;
    line-height: 1.55;
    color: #b6c2d9;
    margin: 0 0 18px;
}
.mum-perks {
    list-style: none;
    text-align: left;
    margin: 0 0 20px;
    padding: 14px 16px;
    background: rgba(18, 53, 110, 0.28);
    border: 1px solid rgba(143, 163, 196, 0.18);
    border-radius: 12px;
}
.mum-perks li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    line-height: 1.45;
    color: #e8edf7;
    padding: 5px 0;
}
.mum-perks li::before {
    content: '';
    flex: 0 0 15px;
    width: 15px;
    height: 15px;
    margin-top: 2px;
    background: url('https://sm.naughtycdn.com/images/landers/prize/NA-Star.png') no-repeat center / contain;
}
.mum-cta {
    display: block;
    width: 100%;
    padding: 15px 16px;
    background: linear-gradient(180deg, #E4163C 0%, #C8102E 100%);
    color: #fff;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.01em;
    border-radius: 12px;
    text-decoration: none;
    box-shadow: 0 8px 24px rgba(200, 16, 46, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.22);
    transition: transform 0.12s, box-shadow 0.12s, filter 0.12s;
}
.mum-cta:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
    box-shadow: 0 11px 30px rgba(200, 16, 46, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.22);
    color: #fff;
    text-decoration: none;
}
.mum-trust {
    margin: 10px 0 0;
    font-size: 11px;
    color: #8fa3c4;
}
.mum-dismiss {
    margin-top: 8px;
    background: none;
    border: none;
    color: #8fa3c4;
    font-size: 13px;
    cursor: pointer;
}
.mum-dismiss:hover { color: #fff; }
</style>
